import { useAuth } from '@/app/AuthContext.jsx';
import { useAllTasksList, useComprobantesPago } from '@/hooks';
import { extractList } from '@/lib/apiList';
import {
  downloadPayinReceipt,
  downloadTaxDocumentPdf,
  errorMessage,
  viewTaxDocumentPdf,
} from '@/lib/recepthor-api';
import { taxDocumentPdfFilename } from '@/lib/taxDocument';
import { useToast } from '@/ui';
import { useMemo } from 'react';

import { ComprobantesPagoTabla } from './ComprobantesPagoTabla';

// Maps a confirmed PAYIN movement (GET /payments) → a payment-receipt row.
// The movement carries no case/tribunal fields by design; the task correlated
// by taskId supplies them.
function mapPayin(p, taskById) {
  const t = taskById.get(p.taskId) ?? null;
  return {
    paymentId: p.paymentId,
    rol: t?.caseRol ?? '—',
    title: t?.tribunalName ?? t?.caseTitle ?? 'Diligencia',
    // The charged total the provider confirmed. The server masks it (null)
    // until the movement is SUCCESS; the query only asks for SUCCESS rows.
    amount: p.settledAmount ?? null,
    // settledAt is the provider confirmation instant — the payout to the
    // receptor never moves this date.
    paidAt: p.settledAt ?? null,
    // Own comprobante archived in S3 (the movement's voucher); false while
    // archiving is in flight.
    hasReceipt: p.hasReceipt === true,
    // Tax documents of the charge: the SII invoice and the credit notes that
    // correct it. They hang off the movement, which is where the model keeps
    // them; absent until the document is emitted, and the controls render
    // disabled while it is.
    taxDocument: p.taxDocument ?? null,
    creditNotes: Array.isArray(p.creditNotes) ? p.creditNotes : [],
  };
}

// Lists the payins the lawyer has made (payin confirmed by the provider), each
// with a button to download the platform's own comprobante. The button is
// always present; it renders disabled ("Comprobante no disponible") until the
// receipt is archived (hasReceipt), then downloads via the org-gated receipt
// endpoint, keyed by the movement's paymentId.
export default function ComprobantesPago() {
  const { organizationId } = useAuth();
  const toast = useToast();
  const { data, isLoading, isError } = useComprobantesPago(organizationId);

  // Task-side columns (rol, tribunal) for the movement rows, every page: a
  // task past the first 100 is otherwise a permanent '—'. Auxiliary: a
  // failed tasks read renders '—' cells, never this page's error state (that
  // one belongs to the payments query).
  const { data: tasksData } = useAllTasksList(
    {},
    { enabled: organizationId != null },
  );
  const taskById = useMemo(() => {
    const map = new Map();
    for (const t of extractList(tasksData)) map.set(t.id, t);
    return map;
  }, [tasksData]);

  // Every row is a provider-confirmed payin: the query filters server-side
  // with type=PAYIN&status=SUCCESS — no client-side re-filtering.
  const rows = extractList(data).map((p) => mapPayin(p, taskById));

  const handleDownload = (row) =>
    downloadPayinReceipt(row.paymentId, {
      organizationId,
      filename: row.rol !== '—' ? `comprobante-${row.rol}.pdf` : undefined,
    }).catch((err) =>
      toast({
        kind: 'err',
        message: errorMessage(err, 'No se pudo descargar el comprobante.'),
      }),
    );

  const handleViewFactura = (doc, rol, prefix = 'factura') =>
    viewTaxDocumentPdf(doc.id, {
      organizationId,
      filename: taxDocumentPdfFilename(doc, { prefix, rol }),
    }).catch((err) =>
      toast({
        kind: 'err',
        message: errorMessage(err, 'No se pudo abrir la factura.'),
      }),
    );

  const handleDownloadFactura = (doc, rol, prefix = 'factura') =>
    downloadTaxDocumentPdf(doc.id, {
      organizationId,
      filename: taxDocumentPdfFilename(doc, { prefix, rol }),
    }).catch((err) =>
      toast({
        kind: 'err',
        message: errorMessage(err, 'No se pudo descargar la factura.'),
      }),
    );

  return (
    <ComprobantesPagoTabla
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      onDownload={handleDownload}
      onViewFactura={handleViewFactura}
      onDownloadFactura={handleDownloadFactura}
    />
  );
}
